import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PickupMap from './PickupMap';
import './OrderFormStandalone.css';

const defaultCenter = {
  lat: 36.114647,
  lng: -115.172813,
};

const OrderFormStandalone: React.FC = () => {
  const [pickupAddress, setPickupAddress] = useState<string>('');
  const [pickupCoordinates, setPickupCoordinates] = useState<{ lat: number; lng: number } | null>(
    null,
  );
  const [pickupSuggestions, setPickupSuggestions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [pickupDate, setPickupDate] = useState<string>('');
  const [pickupHour, setPickupHour] = useState<string>('12');
  const [pickupMinute, setPickupMinute] = useState<string>('00');
  const [pickupAmPm, setPickupAmPm] = useState<'AM' | 'PM'>('AM');
  const [description, setDescription] = useState<string>('');
  const [dateTimeError, setDateTimeError] = useState<string | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [isOutOfRange, setIsOutOfRange] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    console.log('[DEBUG] isOutOfRange:', isOutOfRange);
  }, [isOutOfRange]);

  useEffect(() => {
    if (pickupDate && pickupHour && pickupMinute && pickupAmPm) {
      let hour = parseInt(pickupHour);
      if (pickupAmPm === 'PM' && hour !== 12) hour += 12;
      if (pickupAmPm === 'AM' && hour === 12) hour = 0;
      const minute = parseInt(pickupMinute);

      // parse pickupDate in MM/DD/YYYY format
      const [month, day, year] = pickupDate.split('/').map(Number);
      const selectedDateTime = new Date(year, month - 1, day, hour, minute);

      const now = new Date();
      const minAllowedTime = new Date(now.getTime() + 24 * 60 * 60 * 1000); // 24 hours from now

      if (selectedDateTime.getTime() <= now.getTime()) {
        setDateTimeError('Please select a future date and time.');
      }
      // else if (selectedDateTime.getTime() <= minAllowedTime.getTime()) {
      //   setDateTimeError('Please select a time at least 24 hours in the future.');
      // }
      else {
        setDateTimeError(null);
      }
    } else {
      setDateTimeError(null);
    }
  }, [pickupDate, pickupHour, pickupMinute, pickupAmPm]);

  const handlePickupAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPickupAddress(value);

    if (!value.trim()) {
      setIsOutOfRange(false); // ✅ Reset the error if field is empty
      setPickupSuggestions([]);
      return;
    }

    if (value.length > 2 && window.google) {
      const service = new window.google.maps.places.AutocompleteService();
      service.getPlacePredictions({ input: value }, (predictions) => {
        setPickupSuggestions(predictions || []);
      });
    } else {
      setPickupSuggestions([]);
    }
  };

  const handlePickupSuggestionClick = (sugg: google.maps.places.AutocompletePrediction) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ placeId: sugg.place_id }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const location = results[0].geometry.location;
        const coords = {
          lat: location.lat(),
          lng: location.lng(),
        };

        // Compute distance from origin
        const selectedLatLng = new window.google.maps.LatLng(coords.lat, coords.lng);
        const originLatLng = new window.google.maps.LatLng(defaultCenter.lat, defaultCenter.lng);
        const distanceMeters = window.google.maps.geometry.spherical.computeDistanceBetween(
          originLatLng,
          selectedLatLng,
        );
        const distanceMiles = distanceMeters / 1609.34;

        const isTooFar = distanceMiles > 50; // Change 50 if needed

        setPickupAddress(results[0].formatted_address);
        setPickupSuggestions([]);
        setPickupCoordinates(coords);
        setIsOutOfRange(isTooFar);
      }
    });
  };

  const onSubmit = () => {
    if (!pickupAddress.trim()) {
      setAddressError('Please enter a pickup address.');
      return;
    }

    if (isOutOfRange) {
      setAddressError('Pickup location is outside the allowed area.');
      return;
    }

    setAddressError(null);

    const formData = {
      pickupAddress,
      pickupCoordinates,
      pickupDate,
      pickupHour,
      pickupMinute,
      pickupAmPm,
      description,
    };

    sessionStorage.setItem('orderFormData', JSON.stringify(formData));
    navigate('/comparison');
  };

  const hours = [...Array(12)].map((_, i) => String(i + 1).padStart(2, '0'));
  const minutes = ['00', '30'];

  return (
    <div className='standalone-order-form-container'>
      <div className='standalone-titles'>
        <p className='standalone-order-form-title'>Schedule Your Elite Service</p>
        <p className='standalone-order-form-title'>Every detail refined. Every moment protected.</p>
      </div>
      <div className='standalone-form-section'>
        <div className='standalone-form-container'>
          <div className='standalone-map-container'>
            <PickupMap
              address={pickupAddress}
              onAddressSelect={setPickupAddress}
              onCoordinatesSelect={setPickupCoordinates}
              onOutOfRangeChange={setIsOutOfRange}
              origin={defaultCenter}
              radiusInMiles={10}
            />
          </div>
        </div>

        <div className='standalone-form-container2'>
          <div className='standalone-form-group'>
            <input
              type='text'
              value={pickupAddress}
              onChange={handlePickupAddressChange}
              placeholder='Pickup Location'
              className='standalone-input-field'
            />
            {addressError && <p className='standalone-error-text'>{addressError}</p>}
            {isOutOfRange && <p className='standalone-error-text'>Choose a closer location.</p>}
            {pickupSuggestions.length > 0 && (
              <ul className='standalone-suggestions-list'>
                {pickupSuggestions.slice(0, 3).map((s, i) => (
                  <li
                    key={i}
                    onClick={() => handlePickupSuggestionClick(s)}
                    className='standalone-suggestion-item'
                  >
                    {s.description}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className='standalone-form-group'>
            <input
              type='text'
              value={pickupDate}
              onChange={(e) => {
                const inputValue = e.target.value;

                // When input type is 'date', it gives YYYY-MM-DD
                if (/^\d{4}-\d{2}-\d{2}$/.test(inputValue)) {
                  const [year, month, day] = inputValue.split('-');
                  setPickupDate(`${month}/${day}/${year}`);
                } else {
                  setPickupDate(inputValue); // fallback for manual edits
                }
              }}
              onFocus={(e) => (e.target.type = 'date')}
              onBlur={(e) => (e.target.type = 'text')}
              className='standalone-input-field'
              placeholder='Date'
            />
            {dateTimeError && <p className='standalone-error-text2'>{dateTimeError}</p>}
          </div>

          <div className='standalone-form-row'>
            <div className='standalone-time-group'>
              <div className='standalone-time-selectors'>
                <select value={pickupHour} onChange={(e) => setPickupHour(e.target.value)}>
                  {hours.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
                <select value={pickupMinute} onChange={(e) => setPickupMinute(e.target.value)}>
                  {minutes.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
                <select
                  value={pickupAmPm}
                  onChange={(e) => setPickupAmPm(e.target.value as 'AM' | 'PM')}
                >
                  <option value='AM'>AM</option>
                  <option value='PM'>PM</option>
                </select>
              </div>
            </div>
          </div>

          <div className='standalone-prices-button'>
            <button
              onClick={onSubmit}
              className='standalone-submit-button'
              disabled={
                !!dateTimeError || !pickupDate || !pickupHour || !pickupMinute || isOutOfRange
              }
            >
              See Prices
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderFormStandalone;
